/**
 * Rent under a single master lease, split equally.
 *
 * Two flows of money are shown side by side and never merged: what residents
 * have paid into the pool against their rent share, and whether the landlord
 * has been paid. Every figure comes from the backend; nothing here divides
 * money.
 *
 * Three tabs, as Furniture Rental has them: the month's settlement, the
 * agreement that divides the rent, and the lease with its landlord and
 * deposit. The open tab is kept in the address (`?tab=agreement`), so a link
 * or a reload lands on it.
 */
import { useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Building2,
  Check,
  Clock,
  Layers,
  Percent,
  Receipt,
  Settings,
  ShieldCheck,
  Trash2,
  Users,
} from 'lucide-react'
import { Page, useApp } from '../../components/Layout'
import { RentAgreementCard } from './RentAgreement'
import { LandlordProfileCard, TenancyTermsCard } from './RentConfig'
import { useDepositSummary } from './SecurityDeposits'
import { AsyncSection, Callout, ErrorNotice, Modal } from '../../components/Primitives'
import {
  ResidentCollectionTable,
  type CollectionStatus,
  type ResidentCollectionItem,
} from '../../components/ResidentCollectionTable'
import { DeleteTransactionDialog, type PendingDeletion } from '../../components/DeleteTransactionDialog'
import { BillGstModal, rateText } from '../../components/BillGstModal'
import { ServiceDisbursementDialog } from '../../components/ServiceDisbursementDialog'
import { PAYMENT_MODES, ServicePaymentDialog } from '../../components/ServicePaymentDialog'
import {
  KittyReimbursementPanel,
  KittyReimbursementStatementModal,
  ReimburseDialog,
} from '../../components/KittyReimbursement'
import {
  ProviderPayoutStatementModal,
  ProviderPayoutTable,
  type PayoutColumns,
} from '../../components/ProviderPayoutTable'
import { useToast } from '../../components/Toast'
import {
  BUTTON_PRIMARY,
  BUTTON_QUIET,
  EmptyBlock,
  MiniStat,
  Panel,
  Pill,
  Rupees,
  TransferDestination,
  type Tone,
} from '../../components/ui'
import { api } from '../../api/client'
import { useMutation, useResource } from '../../lib/useApi'
import { LeaseNoticeBanner } from './LeaseLifecycle'
import { formatDate, formatInr, formatMonth, todayIso } from '../../lib/format'
import type {
  Apartment,
  RentCollectionStatus,
  KittyReimbursement,
  RentCycle,
  RentDisbursement,
  RentLeaseConfig,
  RentMonthOverview,
  RentParticipant,
  RentPreview,
} from '../../api/types'

/** The page's tabs, as Furniture Rental has them: the open one is kept in the address (`?tab=lease`). */
export const RENT_TABS = [
  { value: 'settlement', label: 'Monthly Settlement', icon: Receipt },
  { value: 'agreement', label: 'Agreement & Rooms', icon: Layers },
  { value: 'lease', label: 'Lease & Deposit', icon: Building2 },
] as const

export type RentTab = (typeof RENT_TABS)[number]['value']

const TABLE_HEAD =
  'border-b border-[var(--border-subtle)] text-[var(--text-tertiary)] uppercase tracking-wider text-[10px] font-bold'

export default function RentPage() {
  const { month, apartment } = useApp()
  return <RentMonth key={month} month={month} apartment={apartment} />
}

/**
 * How long a payment has been past its due date, as `13 days overdue`, or
 * null when it is not yet due. Calendar days, so no time zone can shift it.
 */
function overdueNotice(dueDate: string | null | undefined): string | null {
  if (!dueDate) return null
  const day = (iso: string) => {
    const [year, month, date] = iso.split('-').map(Number)
    return Date.UTC(year, month - 1, date) / 86_400_000
  }
  const days = day(todayIso()) - day(dueDate)
  if (!(days > 0)) return null
  return days === 1 ? '1 day overdue' : `${days} days overdue`
}

export function RentMonth({ month, apartment = null }: { month: string; apartment?: Apartment | null }) {
  const toast = useToast()
  // A month the lease covers is charged by itself and follows the lease; any
  // other shows a preview, from which it can be recorded by hand.
  const overview = useResource<RentMonthOverview>(`/api/rent/cycle/${month}`)
  const [dialog, setDialog] = useState<'payout' | 'billing' | null>(null)
  // Whom the kitty is paying back, when it is; whose reimbursement statement is open.
  const [reimbursing, setReimbursing] = useState<KittyReimbursement | null>(null)
  const [viewingReimbursement, setViewingReimbursement] = useState<string | null>(null)
  // A payment or payout recorded by mistake, about to be deleted.
  const [deleting, setDeleting] = useState<PendingDeletion | null>(null)
  const [payingFor, setPayingFor] = useState<RentParticipant | null>(null)
  const [viewingStatement, setViewingStatement] = useState<RentParticipant | null>(null)
  const [viewingLandlordStatement, setViewingLandlordStatement] = useState(false)
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  const tab: RentTab = RENT_TABS.some((entry) => entry.value === requested) ? (requested as RentTab) : 'settlement'
  // The month as the last payment or payout left it, for what its toast says.
  const saved = useRef<RentCycle | null>(null)

  return (
    <AsyncSection resource={overview} loadingLabel="Loading rent…">
      {(data) => {
        const { config, cycle, preview } = data
        // While the month's statement is open, a transaction recorded by mistake can be deleted.
        const open = Boolean(cycle?.transactions_open)
        const deletePayment = (paymentId: string, title: string, what: string) =>
          setDeleting({ title, what, url: `/api/rent/cycle/${month}/payments/${encodeURIComponent(paymentId)}` })
        return (
          <Page
            title="Rent"
            subtitle="What residents have paid towards this month's rent, and whether the landlord has been paid, under the master lease."
            breadcrumbs={[{ label: 'Services & Utilities', to: '/services' }, { label: 'Rent' }]}
          >
            {!config ? (
              <Panel
                title="Set up the lease"
                icon={<Building2 className="w-4 h-4 shrink-0 text-[var(--accent)]" aria-hidden="true" />}
              >
                <EmptyBlock
                  title="No lease on file yet"
                  body="Enter the landlord and the monthly rent once. Each month's rent is then split equally between the residents taking part."
                  action={
                    <Link to="/housing?tab=lease" className={BUTTON_PRIMARY}>
                      <Settings className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                      Set up lease
                    </Link>
                  }
                />
              </Panel>
            ) : (
              <>
                <div className="tabs" role="tablist" aria-label="Rent">
                  {RENT_TABS.map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      id={`rent-tab-${value}`}
                      type="button"
                      role="tab"
                      className="tab"
                      aria-selected={tab === value}
                      aria-controls={`rent-panel-${value}`}
                      onClick={() => setParams(value === 'settlement' ? {} : { tab: value }, { replace: true })}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0 inline-block mr-1.5 align-[-2px]" aria-hidden="true" />
                      {label}
                    </button>
                  ))}
                </div>
                <div role="tabpanel" id={`rent-panel-${tab}`} aria-labelledby={`rent-tab-${tab}`} className="space-y-4">
                  {tab === 'settlement' && (
                    <>
                      <LeaseNoticeBanner
                        config={config}
                        participantCount={(cycle ?? preview)?.participant_count ?? 0}
                        onChanged={(message, detail) => {
                          toast.success(message, detail)
                          overview.reload()
                        }}
                      />
                      {/* The month's two steps, in order: collect from residents, then
                          pay the landlord. */}
                      {cycle ? (
                        <CyclePanel
                          cycle={cycle}
                          config={config}
                          blockedReason={data.live_blocked_reason ?? null}
                          onPay={setPayingFor}
                          onViewStatement={setViewingStatement}
                        />
                      ) : (
                        preview && (
                          <PreviewPanel
                            month={month}
                            preview={preview}
                            // A month not begun is only a preview; any other can be charged by hand.
                            onRecorded={
                              data.month_state === 'upcoming'
                                ? undefined
                                : (recorded) => {
                                    toast.success(
                                      'Rent recorded',
                                      `${formatMonth(month)} rent of ${formatInr(recorded.total_rent_paise)} is open for collection.`,
                                    )
                                    overview.reload()
                                  }
                            }
                          />
                        )
                      )}
                      <LandlordPanel
                        config={config}
                        cycle={cycle}
                        onPayout={() => setDialog('payout')}
                        onViewStatement={() => setViewingLandlordStatement(true)}
                      />
                      {/* Whoever paid the landlord more than their share is paid back from the kitty. */}
                      {(cycle?.reimbursements ?? []).length > 0 && (
                        <KittyReimbursementPanel
                          reimbursements={cycle!.reimbursements!}
                          onReimburse={setReimbursing}
                          onViewStatement={(owed) => setViewingReimbursement(owed.recipient_resident_id)}
                        />
                      )}
                    </>
                  )}
                  {/* How the rent is divided: equally, or by each room's base rent. */}
                  {tab === 'agreement' && (
                    <>
                      <RentBillPanel
                        month={month}
                        config={config}
                        source={cycle ?? preview}
                        // Only a month charged and still open has a bill to set.
                        onConfigure={cycle?.cycle_state === 'live' ? () => setDialog('billing') : undefined}
                      />
                      <RentAgreementCard
                        config={config}
                        cycle={cycle}
                        preview={preview}
                        roomTiered={data.room_tiered ?? preview?.room_tiered ?? null}
                        month={month}
                        onSaved={overview.reload}
                      />
                    </>
                  )}
                  {tab === 'lease' && <LeasePanels config={config} onSaved={overview.reload} />}
                </div>
              </>
            )}

            {reimbursing && (
              <ReimburseDialog
                month={month}
                reimbursement={reimbursing}
                submit={(details) =>
                  api.post<RentCycle>(`/api/rent/cycle/${month}/reimburse`, {
                    recipient_resident_id: reimbursing.recipient_resident_id,
                    ...details,
                  })
                }
                onClose={() => setReimbursing(null)}
                onDone={(amount) => {
                  const name = reimbursing.recipient_name
                  setReimbursing(null)
                  toast.success('Reimbursement recorded', `${formatInr(amount)} from the kitty to ${name}`)
                  overview.reload()
                }}
              />
            )}
            {dialog === 'billing' && cycle && config && (
              <BillGstModal
                month={month}
                title="Configure Rent GST & Total"
                description={`${formatMonth(month)} rent: Tax/GST on the lease, and the actual total paid to the landlord.`}
                subtotalPaise={cycle.subtotal_base_paise ?? config.monthly_rent_paise}
                subtotalLabel="Rent Subtotal"
                subtotalOwner="the lease rent's"
                gstRatePercent={cycle.gst_rate_percent ?? 0}
                defaultGstRatePercent={0}
                chargedTotalOverridePaise={cycle.charged_total_override_paise}
                isBilled
                onSave={(values) =>
                  api.post<RentMonthOverview>(`/api/rent/cycle/${month}/billing`, values).then((updated) => {
                    setDialog(null)
                    toast.success(
                      'Rent bill updated',
                      `${formatMonth(month)} rent is ${formatInr(updated.cycle?.total_rent_paise ?? cycle.total_rent_paise)}.`,
                    )
                    overview.reload()
                  })
                }
                onClose={() => setDialog(null)}
              />
            )}
            {dialog === 'payout' && cycle && config && (
              <ServiceDisbursementDialog
                title="Record Landlord Payout"
                month={month}
                payeeName={config.landlord_name || 'Landlord'}
                payeeRole="landlord"
                remainingPaise={cycle.remaining_disbursement_paise}
                totalBillPaise={cycle.total_rent_paise}
                suggestedPaise={collectedForPayout(cycle)}
                residents={cycle.participants.map(({ resident_id, display_name }) => ({ resident_id, display_name }))}
                destination={(amountPaise) => (
                  <TransferDestination
                    label="Payout destination"
                    name={config.payout_beneficiary_name || config.landlord_name}
                    amountPaise={amountPaise}
                    bankAccount={config.landlord_bank_account}
                    bankIfsc={config.landlord_bank_ifsc}
                    upiId={config.landlord_upi_id}
                    missing={
                      <>
                        No bank account or UPI ID is recorded for the landlord.{' '}
                        <Link to="/payments" className="font-semibold">
                          Add them on Payment Pools
                        </Link>
                        .
                      </>
                    }
                  />
                )}
                onSave={({ notes, ...payout }) =>
                  api
                    .post<RentCycle>(`/api/rent/cycle/${month}/disburse`, { ...payout, note: notes })
                    .then((updated) => {
                      saved.current = updated
                    })
                }
                onClose={() => setDialog(null)}
                onDone={() => {
                  const updated = saved.current
                  setDialog(null)
                  toast.success(
                    'Landlord payout recorded',
                    updated?.warnings?.[0] ??
                      (updated?.disbursement_status === 'partially_disbursed'
                        ? `${formatInr(updated.remaining_disbursement_paise)} is still owed to the landlord.`
                        : undefined),
                  )
                  overview.reload()
                }}
              />
            )}
            {viewingLandlordStatement && cycle && config && (
              <ProviderPayoutStatementModal
                title="Landlord Payout Statement"
                name={config.landlord_name}
                month={month}
                columns={LANDLORD_COLUMNS}
                totalPaise={cycle.total_rent_paise}
                paidPaise={cycle.disbursed_paise}
                stillOwedPaise={cycle.remaining_disbursement_paise}
                status={cycle.disbursement_status}
                disbursements={cycle.disbursements}
                caption="Payments to the landlord"
                onDeleteDisbursement={
                  open
                    ? (tranche) =>
                        setDeleting({
                          title: 'Delete this payout?',
                          what: `${formatInr(tranche.amount_paise)} paid to the landlord on ${formatDate(tranche.disbursed_at)}${tranche.disbursed_by === 'pool' ? ' from the House Kitty' : ` by ${tranche.funding_resident_name ?? 'a resident'}`}.`,
                          url: `/api/rent/cycle/${month}/disbursements/${encodeURIComponent(tranche.disbursement_id)}`,
                        })
                    : undefined
                }
                onClose={() => setViewingLandlordStatement(false)}
              />
            )}
            {viewingStatement && cycle && (
              <RentStatementModal
                month={month}
                dueDate={cycle.due_date}
                // Read from the month as it now stands, not as it was when opened.
                participant={
                  cycle.participants.find(
                    (row) => row.resident_id === viewingStatement.resident_id,
                  ) ?? viewingStatement
                }
                disbursements={cycle.disbursements}
                onDeletePayment={
                  open
                    ? (entry) =>
                        deletePayment(
                          entry.paymentId!,
                          'Delete this payment?',
                          `${viewingStatement.display_name}'s ${formatInr(entry.amount_paise)} payment of ${formatDate(entry.date)}.`,
                        )
                    : undefined
                }
                onClose={() => setViewingStatement(null)}
              />
            )}
            {viewingReimbursement &&
              (() => {
                // Read from the month as it now stands, so a deleted reimbursement drops out at once.
                const owed = (cycle?.reimbursements ?? []).find(
                  (row) => row.recipient_resident_id === viewingReimbursement,
                )
                return (
                  owed && (
                    <KittyReimbursementStatementModal
                      month={month}
                      reimbursement={owed}
                      onDelete={
                        open
                          ? (refund) =>
                              deletePayment(
                                refund.payment_id,
                                'Delete this reimbursement?',
                                `${formatInr(refund.amount_paise)} paid back from the kitty to ${owed.recipient_name} on ${formatDate(refund.payment_date)}.`,
                              )
                          : undefined
                      }
                      onClose={() => setViewingReimbursement(null)}
                    />
                  )
                )
              })()}
            {deleting && (
              <DeleteTransactionDialog
                deletion={deleting}
                onClose={() => setDeleting(null)}
                onDeleted={() => {
                  setDeleting(null)
                  toast.success('Deleted', 'The month owes what it owed before.')
                  overview.reload()
                }}
              />
            )}
            {payingFor && cycle && (
              <ServicePaymentDialog
                title="Record Rent Payment"
                month={month}
                serviceLabel="rent"
                residentName={payingFor.display_name}
                outstandingPaise={payingFor.outstanding_paise}
                // Residents pay their share into the House Kitty.
                destination={(amountPaise) => (
                  <TransferDestination
                    name={apartment?.pool_name || 'House Kitty'}
                    amountPaise={amountPaise}
                    bankAccount={apartment?.pool_bank_account}
                    bankIfsc={apartment?.pool_bank_ifsc}
                    upiId={apartment?.pool_upi_id}
                    missing={
                      <>
                        No bank account or UPI ID is recorded for the House Kitty.{' '}
                        <Link to="/payments" className="font-semibold">
                          Add them on Payment Pools
                        </Link>
                        .
                      </>
                    }
                  />
                )}
                onSave={(payment) =>
                  api
                    .post<RentCycle>(`/api/rent/cycle/${month}/pay`, {
                      resident_id: payingFor.resident_id,
                      ...payment,
                    })
                    .then((updated) => {
                      saved.current = updated
                    })
                }
                onClose={() => setPayingFor(null)}
                onDone={() => {
                  const updated = saved.current
                  const name = payingFor.display_name
                  const row = updated?.participants.find(
                    (item) => item.resident_id === payingFor.resident_id,
                  )
                  setPayingFor(null)
                  toast.success(
                    row?.collection_status === 'paid'
                      ? `${name}'s rent is paid`
                      : `Payment from ${name} recorded`,
                    row && row.outstanding_paise > 0
                      ? `${formatInr(row.outstanding_paise)} of this month's share is still owed.`
                      : updated?.payment && updated.payment.unapplied_credit_paise > 0
                        ? `${formatInr(updated.payment.unapplied_credit_paise)} is left as unapplied credit.`
                        : undefined,
                  )
                  overview.reload()
                }}
              />
            )}
          </Page>
        )
      }}
    </AsyncSection>
  )
}

/* --------------------------------------------------------- the agreement -- */

/**
 * The month's rent as a bill: rent on a residential lease carries no GST, so
 * it bills the rent as it is.
 */
function RentBillPanel({
  month,
  config,
  source,
  onConfigure,
}: {
  month: string
  config: RentLeaseConfig
  source: RentCycle | RentPreview | null
  /** Set the month's GST and total; left out for a month that cannot change. */
  onConfigure?: () => void
}) {
  const subtotal = source?.subtotal_base_paise ?? source?.total_rent_paise ?? config.monthly_rent_paise
  const rate = source?.gst_rate_percent ?? 0
  const gst = source?.total_gst_paise ?? 0
  const total = source?.total_rent_paise ?? subtotal
  const overridden = source?.charged_total_override_paise != null
  return (
    <Panel
      title="Monthly Bill"
      icon={<Receipt className="w-4 h-4 shrink-0 text-[var(--accent)]" aria-hidden="true" />}
      hint="Rent on a residential lease carries no GST unless the month is given a rate, or the invoice's own total."
      menuActions={onConfigure ? [{ label: 'Configure GST & Total', icon: Percent, onClick: onConfigure }] : undefined}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MiniStat label="Rent Subtotal" value={<Rupees paise={subtotal} />} note={`For ${formatMonth(month)}`} />
        <MiniStat
          label={`Tax / GST (${rateText(rate)}%)`}
          value={<Rupees paise={gst} />}
          note={overridden ? 'As invoiced' : rate === 0 ? 'Exempt' : undefined}
        />
        <MiniStat
          label="Gross Rent Total"
          value={<Rupees paise={total} />}
          note={
            overridden
              ? `Calculated: ${formatInr(source?.calculated_total_paise ?? subtotal)}`
              : rate === 0
                ? 'No GST'
                : 'GST included'
          }
        />
      </div>
    </Panel>
  )
}

/* ------------------------------------------------------------- the lease -- */

/**
 * The lease the rent is charged under: its terms, who the landlord is, and the
 * security deposit held against it. The terms are edited on Housing, where the
 * lease is kept; the landlord's details on their contact.
 */
function LeasePanels({ config, onSaved }: { config: RentLeaseConfig; onSaved: () => void }) {
  const navigate = useNavigate()
  return (
    <>
      <TenancyTermsCard config={config} onEdit={() => navigate('/housing?tab=lease')} />
      <LandlordProfileCard config={config} onSaved={onSaved} />
      <DepositSummaryPanel />
    </>
  )
}

/** The lease's security deposit: what it is, what flatmates have paid towards it, and what is still to come. */
function DepositSummaryPanel() {
  const deposits = useDepositSummary()
  return (
    <Panel
      title="Security Deposit"
      icon={<ShieldCheck className="w-4 h-4 shrink-0 text-[var(--accent)]" aria-hidden="true" />}
      hint="Held by the landlord against the lease; each flatmate's part, and every refund, is kept under Housing."
      actions={
        <Link to="/housing?tab=deposits" className={BUTTON_QUIET}>
          Manage deposits
        </Link>
      }
    >
      <AsyncSection resource={deposits} loadingLabel="Loading the deposit…">
        {(summary) => (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" aria-label="Security deposit">
            <MiniStat
              label="Lease Deposit"
              value={<Rupees paise={summary.master_deposit_paise} />}
              note={summary.master_deposit_paise == null ? 'Not entered on the lease' : 'As agreed on the lease'}
            />
            <MiniStat
              label="Paid In"
              value={<Rupees paise={summary.total_funded_paise} />}
              note={`of ${formatInr(summary.total_target_paise)} across ${summary.total_slots_count} slot${summary.total_slots_count === 1 ? '' : 's'}`}
            />
            <MiniStat
              label="Still to Fund"
              value={<Rupees paise={summary.unfunded_gap_paise} />}
              note={summary.unfunded_gap_paise > 0 ? 'Awaiting flatmates' : 'Fully funded'}
            />
          </div>
        )}
      </AsyncSection>
    </Panel>
  )
}

const COLLECTION: Record<RentCollectionStatus, { tone: Tone; label: string }> = {
  paid: { tone: 'success', label: 'Paid' },
  partial: { tone: 'warning', label: 'Part paid' },
  unpaid: { tone: 'warning', label: 'Unpaid' },
  funded_payout: { tone: 'success', label: 'Paid' },
}

function CyclePanel({
  cycle,
  config,
  blockedReason,
  onPay,
  onViewStatement,
}: {
  cycle: RentCycle
  config: RentLeaseConfig
  /** Why the open month cannot follow the lease as it stands: it stays as it was. */
  blockedReason: string | null
  onPay: (participant: RentParticipant) => void
  onViewStatement: (participant: RentParticipant) => void
}) {
  // Rent is due by the landlord's due date; a share still owed after it is overdue.
  const overdue = overdueNotice(cycle.due_date)
  const items: ResidentCollectionItem[] = cycle.participants.map((participant) => {
    const funded = participant.collection_status === 'funded_payout'
    const settled = funded || participant.collection_status === 'paid'
    // Paying the landlord directly counts towards their share, as paying into the kitty does.
    const coveredPaise = Math.min(participant.share_paise, participant.settled_paise + participant.advanced_paise)
    const stillOwedPaise = Math.max(0, participant.share_paise - coveredPaise)
    const status: CollectionStatus = settled
      ? 'paid'
      : participant.collection_status === 'partial'
        ? 'partial'
        : 'unpaid'
    return {
      id: participant.resident_id,
      name: participant.display_name,
      href: `/residents/${participant.resident_id}`,
      sharePaise: participant.share_paise,
      paidIntoKittyPaise: coveredPaise,
      stillOwedPaise,
      overdueNotice: stillOwedPaise > 0 && overdue ? overdue : undefined,
      status,
      canPay: !settled && participant.outstanding_paise > 0,
      onPay: () => onPay(participant),
      onViewStatement: () => onViewStatement(participant),
    }
  })
  return (
    <Panel
      title="Resident Collection"
      icon={<Users className="w-4 h-4 shrink-0 text-[var(--accent)]" aria-hidden="true" />}
      hint="What each resident owes for this month's rent, and how much of it has been collected into the House Kitty."
    >
      {blockedReason && (
        <p className="m-0 text-xs font-medium text-[var(--warning)]" role="status">
          {blockedReason} This month stays as charged until it does.
        </p>
      )}
      {!blockedReason && !cycle.matches_lease_rent && (
        <p className="m-0 text-xs text-[var(--warning)]">
          This month was charged at {formatInr(cycle.total_rent_paise)}; the lease now says{' '}
          {formatInr(cycle.lease_rent_paise ?? config.monthly_rent_paise)}. A closed month keeps its own amount.
        </p>
      )}

      <ResidentCollectionTable caption="Rent shares and collection" items={items} />

      {cycle.excluded_residents.length > 0 && (
        <p className="m-0 text-[11px] text-[var(--text-secondary)]">
          Not taking part this month:{' '}
          {cycle.excluded_residents.map((resident) => resident.display_name).join(', ')}.
        </p>
      )}
    </Panel>
  )
}

function PreviewPanel({
  month,
  preview,
  onRecorded,
}: {
  month: string
  preview: RentPreview
  onRecorded?: (cycle: RentCycle) => void
}) {
  // Charging the month opens it for collection: each share can then be paid.
  const record = useMutation(() => api.post<RentCycle>(`/api/rent/cycle/${month}/generate`, {}), {
    onSuccess: onRecorded,
  })
  // The split follows the agreement model; it is changed on the agreement card.
  const tiered = preview.split_mode === 'room_tiered'
  const rooms = new Map(preview.room_tiered.shares.map((row) => [row.resident_id, row.room_number]))
  const items: ResidentCollectionItem[] = preview.participants.map((row) => ({
    id: row.resident_id,
    name: row.display_name,
    href: `/residents/${row.resident_id}`,
    subtitle: tiered ? (rooms.get(row.resident_id) ?? 'No room') : undefined,
    sharePaise: row.share_paise,
    // Nothing is owed, or can be paid, until the month is charged.
    paidIntoKittyPaise: null,
    stillOwedPaise: null,
    status: null,
    canPay: false,
  }))
  return (
    <Panel
      title="Resident Collection"
      icon={<Users className="w-4 h-4 shrink-0 text-[var(--accent)]" aria-hidden="true" />}
      hint={
        tiered
          ? "Each resident taking part is charged their room's base rent. A partial month never reduces a share."
          : 'Everyone taking part is charged a full equal share. A partial month never reduces a share.'
      }
    >
      {preview.blocked_reason && (
        <p className="m-0 text-xs font-medium text-[var(--warning)]" role="status">
          {preview.blocked_reason}
        </p>
      )}
      {record.error && <ErrorNotice error={record.error} />}
      {items.length > 0 && (
        <ResidentCollectionTable caption={`Rent shares for ${formatMonth(month)}`} items={items} />
      )}
      {preview.excluded_residents.length > 0 && (
        <p className="m-0 text-[11px] text-[var(--text-secondary)]">
          Not taking part:{' '}
          {preview.excluded_residents.map((resident) => resident.display_name).join(', ')}.
        </p>
      )}
      {onRecorded && (
        <div className="flex justify-end">
          <button
            type="button"
            className={BUTTON_PRIMARY}
            disabled={!preview.can_generate || record.pending}
            onClick={() => void record.run(undefined)}
          >
            <Receipt className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            {record.pending ? 'Recording…' : `Record Rent for ${formatMonth(month)}`}
          </button>
        </div>
      )}
    </Panel>
  )
}

/** The payout table's and statement's headings, for the landlord. */
const LANDLORD_COLUMNS: PayoutColumns = {
  payee: 'Landlord',
  total: 'Total Rent',
  paid: 'Paid to Owner',
}

function LandlordPanel({
  config,
  cycle,
  onPayout,
  onViewStatement,
}: {
  config: RentLeaseConfig
  cycle: RentCycle | null
  onPayout: () => void
  onViewStatement: () => void
}) {
  const tranches = cycle?.disbursements ?? []
  const advanced = tranches.some((tranche) => tranche.disbursed_by === 'resident_advance')
  const overdue = overdueNotice(cycle?.due_date)
  return (
    <Panel
      title="Landlord Payout"
      icon={<Building2 className="w-4 h-4 shrink-0 text-[var(--accent)]" aria-hidden="true" />}
      hint="What is owed to the landlord, and payouts made to them. Select the row for each payment made."
    >
      {/* Laid out as the collection table above, so the two read column for
          column: what was due, what has been paid, what is still owed. */}
      <ProviderPayoutTable
        caption="Rent owed to the landlord"
        columns={LANDLORD_COLUMNS}
        items={[
          {
            id: 'landlord',
            name: config.landlord_name,
            totalPaise: cycle ? cycle.total_rent_paise : config.monthly_rent_paise,
            // Nothing is paid or owed until the month is charged.
            paidPaise: cycle ? cycle.disbursed_paise : null,
            stillOwedPaise: cycle ? cycle.remaining_disbursement_paise : null,
            overdueNotice: overdue ?? undefined,
            status: cycle ? cycle.disbursement_status : null,
            // Open until the landlord has been paid in full: the rent can go in tranches.
            canPay: cycle != null && cycle.disbursement_status !== 'disbursed',
            payLabel: 'Pay now to landlord',
            onPay: onPayout,
            onViewStatement: cycle ? onViewStatement : undefined,
          },
        ]}
      />
      {advanced && (
        <p className="m-0 text-[11px] text-[var(--text-secondary)]">
          A resident who paid the landlord directly paid their own share through the House
          Kitty; what they paid beyond it the kitty pays back to them.
        </p>
      )}
    </Panel>
  )
}

/* --------------------------------------------------- rent statement modal -- */

/** One payment towards a share: into the kitty, or the part of a direct payment to the landlord that covered it. */
interface StatementEntry {
  id: string
  /** Set on a payment into the kitty, which can be deleted while the month is open. */
  paymentId?: string
  date: string
  amount_paise: number
  modeLabel: string | null
  reference: string | null
}

/** One resident's rent for the month: what they owe, and every payment towards it. */
function RentStatementModal({
  month,
  dueDate,
  participant,
  disbursements,
  onDeletePayment,
  onClose,
}: {
  month: string
  dueDate: string | null
  participant: RentParticipant
  disbursements: RentDisbursement[]
  /** Deletes a payment recorded by mistake, while the month is open; without it none is offered. */
  onDeletePayment?: (entry: StatementEntry) => void
  onClose: () => void
}) {
  const status = COLLECTION[participant.collection_status]
  const funded = participant.collection_status === 'funded_payout'
  // Paying the landlord directly counts towards their share, as paying into the kitty does.
  const coveredPaise = Math.min(participant.share_paise, participant.settled_paise + participant.advanced_paise)
  const stillOwedPaise = Math.max(0, participant.share_paise - coveredPaise)
  const overdue = stillOwedPaise > 0 ? overdueNotice(dueDate) : null
  const modes = new Map(PAYMENT_MODES.map((mode) => [mode.value, mode.label]))
  const dash = <span className="text-[var(--text-tertiary)]">—</span>
  const history: StatementEntry[] = participant.payments.map((payment) => ({
    id: payment.payment_id,
    paymentId: payment.payment_id,
    date: payment.payment_date,
    amount_paise: payment.amount_paise,
    modeLabel: payment.payment_mode ? (modes.get(payment.payment_mode) ?? null) : null,
    reference: payment.reference,
  }))
  // What paying the landlord directly covered of the share, tranche by tranche.
  let advanceCover = Math.max(0, coveredPaise - participant.settled_paise)
  for (const tranche of disbursements) {
    if (advanceCover <= 0) break
    if (tranche.disbursed_by === 'resident_advance' && tranche.funding_resident_id === participant.resident_id) {
      const amount = Math.min(tranche.amount_paise, advanceCover)
      advanceCover -= amount
      history.push({
        id: tranche.disbursement_id,
        date: tranche.disbursed_at,
        amount_paise: amount,
        modeLabel: 'Direct to Landlord',
        reference: tranche.disbursement_reference,
      })
    }
  }
  if (advanceCover > 0) {
    // An advance recorded before the month's payouts were kept as tranches.
    history.push({
      id: 'advance-cover',
      date: participant.last_payment_date ?? `${month}-01`,
      amount_paise: advanceCover,
      modeLabel: 'Direct to Landlord',
      reference: null,
    })
  }
  history.sort((a, b) => a.date.localeCompare(b.date))
  return (
    <Modal
      title="Rent Statement"
      description={`${participant.display_name} · ${formatMonth(month)}`}
      onClose={onClose}
    >
      <div className="stack">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <MiniStat label="Share" value={<Rupees paise={participant.share_paise} />} />
          <MiniStat label="Paid into Kitty" value={<Rupees paise={coveredPaise} />} />
          <MiniStat
            label="Still Owed"
            value={<Rupees paise={stillOwedPaise} />}
            note={
              funded ? (
                'Covered by paying the landlord'
              ) : overdue ? (
                <span className="font-semibold text-[var(--danger)]">{overdue}</span>
              ) : undefined
            }
          />
        </div>

        {participant.advanced_paise > 0 && (
          <Callout>
            {participant.display_name} paid {formatInr(participant.advanced_paise)} to the
            landlord directly this month
            {funded ? ', which covers their share' : ''}.
          </Callout>
        )}

        <section aria-label="Payment history" className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="m-0 text-sm font-bold text-[var(--text-primary)]">Payment History</h3>
            <Pill tone={status.tone}>
              {participant.collection_status === 'paid' || funded ? (
                <Check className="w-3 h-3 shrink-0" aria-hidden="true" />
              ) : (
                <Clock className="w-3 h-3 shrink-0" aria-hidden="true" />
              )}
              {status.label}
            </Pill>
          </div>
          {history.length === 0 ? (
            <EmptyBlock title="No payments recorded yet for this month." />
          ) : (
            <div className="overflow-x-auto min-w-0">
              <table className="w-full text-left text-xs min-w-[480px] border-collapse">
                <caption className="sr-only">
                  Payments towards {participant.display_name}'s rent
                </caption>
                <thead>
                  <tr className={TABLE_HEAD}>
                    <th scope="col" className="pb-3 pr-3 font-bold text-center">Date</th>
                    <th scope="col" className="pb-3 pr-3 font-bold text-center">Amount</th>
                    <th scope="col" className="pb-3 pr-3 font-bold text-center">Mode</th>
                    <th scope="col" className="pb-3 pr-3 font-bold text-center">Reference / UTR</th>
                    {onDeletePayment && <th scope="col" className="pb-3 font-bold text-center">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)] font-medium">
                  {history.map((entry) => (
                    <tr key={entry.id} className="align-middle">
                      <td className="py-2.5 pr-3 text-center tabular">{formatDate(entry.date)}</td>
                      <td className="py-2.5 pr-3 text-center font-bold text-[var(--text-primary)]">
                        <Rupees paise={entry.amount_paise} />
                      </td>
                      <td className="py-2.5 pr-3 text-center">{entry.modeLabel ?? dash}</td>
                      <td className="py-2.5 pr-3 text-center tabular break-all text-[var(--text-primary)]">
                        {entry.reference || dash}
                      </td>
                      {onDeletePayment && (
                        <td className="py-2.5 text-center">
                          {entry.paymentId && (
                            <button
                              type="button"
                              className={BUTTON_QUIET}
                              onClick={() => onDeletePayment(entry)}
                              aria-label={`Delete the ${formatInr(entry.amount_paise)} payment of ${formatDate(entry.date)}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                              Delete
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </Modal>
  )
}

/* ----------------------------------------------- landlord statement modal -- */

/** The landlord's side of the month: what is due to them, and every payment made. */
/* ------------------------------------------------------------ the payout -- */

/**
 * The landlord is due on a fixed day and flatmates pay when they can, so a
 * first payout offers what has been collected so far; after that, the rest.
 */
function collectedForPayout(cycle: RentCycle): number | undefined {
  return cycle.disbursements.length === 0 && cycle.collected_paise > 0 && cycle.collected_paise < cycle.total_rent_paise
    ? cycle.collected_paise
    : undefined
}
